import { forwardRef, useEffect, useImperativeHandle, useMemo, useState } from 'react';
import { Button, Form, Spinner } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { AxiosError } from 'axios';
import { useBranchContext } from 'providers/BranchProvider';
import axiosInstance from 'utils/axiosInterceptors';
import { formatLongIsoDateOrDash } from 'utils/formatLongIsoDate';
import { TSatisfyingProcurementInvoiceItem } from 'utils/types';
import { formatPeso } from './EstimatedUnitPriceCell';

const REMOVE_INVOICE_SELECTION = 0;

export type BindPurchaseOrderItemToInvoiceFormHandle = {
  getProcurementInvoiceItemId: () => number | null;
  shouldUnbind: () => boolean;
};

type BindPurchaseOrderItemToInvoiceFormProps = {
  purchaseOrderId: string;
  purchaseOrderItemId: number;
  hasExistingInvoice?: boolean;
};

const BindPurchaseOrderItemToInvoiceForm = forwardRef<
  BindPurchaseOrderItemToInvoiceFormHandle,
  BindPurchaseOrderItemToInvoiceFormProps
>(({ purchaseOrderId, purchaseOrderItemId, hasExistingInvoice }, ref) => {
  const { selectedBranch } = useBranchContext();
  const [items, setItems] = useState<TSatisfyingProcurementInvoiceItem[]>([]);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [showPastInvoices, setShowPastInvoices] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useImperativeHandle(ref, () => ({
    getProcurementInvoiceItemId: () =>
      selectedId != null && selectedId > 0 ? selectedId : null,
    shouldUnbind: () => selectedId === REMOVE_INVOICE_SELECTION
  }));

  useEffect(() => {
    const fetchItems = async () => {
      if (!selectedBranch?.branchDomain) {
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      setErrorMessage(null);
      try {
        const res = await axiosInstance.get<TSatisfyingProcurementInvoiceItem[]>(
          `/api/v1/${selectedBranch.branchDomain}/purchaseOrders/${purchaseOrderId}/items/${purchaseOrderItemId}/satisfyingInvoiceItems`
        );
        const nextItems = res.data;
        setItems(nextItems);
        const currentlyLinked = nextItems.find(item => item.isLinkedToCurrentItem);
        setSelectedId(currentlyLinked?.procurementInvoiceItemId ?? null);
      } catch (error) {
        if (error instanceof AxiosError) {
          setErrorMessage(
            error.response?.data?.errorMessage ||
              'Could not load matching invoices.'
          );
        } else {
          setErrorMessage('Could not load matching invoices.');
        }
      } finally {
        setIsLoading(false);
      }
    };

    void fetchItems();
  }, [purchaseOrderId, purchaseOrderItemId, selectedBranch?.branchDomain]);

  const hasCurrentLink = useMemo(
    () =>
      Boolean(hasExistingInvoice) ||
      items.some(item => item.isLinkedToCurrentItem),
    [hasExistingInvoice, items]
  );
  const defaultItems = useMemo(
    () =>
      items.filter(
        item => item.isAfterPurchaseOrder !== false || item.isLinkedToCurrentItem
      ),
    [items]
  );
  const pastItems = useMemo(
    () =>
      items.filter(
        item =>
          item.isAfterPurchaseOrder === false && !item.isLinkedToCurrentItem
      ),
    [items]
  );

  const renderItem = (
    item: TSatisfyingProcurementInvoiceItem,
    selectable: boolean
  ) => {
    const inputId = `bind-invoice-item-${item.procurementInvoiceItemId}`;
    const linkedLabel = item.isLinkedToCurrentItem
      ? 'Currently linked to this item'
      : item.linkedPurchaseOrderIds.length > 0
        ? `Already on PO ${item.linkedPurchaseOrderIds
            .map(id => `#${id}`)
            .join(', ')}`
        : 'Not yet bound to a PO';
    const isPastInvoice = item.isAfterPurchaseOrder === false;

    return (
      <Form.Check
        key={item.procurementInvoiceItemId}
        type="radio"
        id={inputId}
        name="satisfying-invoice-item"
        className="mb-3"
        disabled={!selectable}
        checked={selectedId === item.procurementInvoiceItemId}
        onChange={() => {
          if (selectable) {
            setSelectedId(item.procurementInvoiceItemId);
          }
        }}
        label={
          <div>
            <div className="fw-semibold">
              <Link
                to={`/procurement-invoices/${item.procurementInvoiceId}`}
                target="_blank"
                rel="noreferrer"
                onClick={event => event.stopPropagation()}
              >
                Invoice {item.invoiceNumber || `#${item.procurementInvoiceId}`}
              </Link>
              {item.isLinkedToCurrentItem ? (
                <span className="fs-10 text-body-tertiary ms-2">Current</span>
              ) : null}
            </div>
            <div className="fs-9 text-body-secondary">
              {formatLongIsoDateOrDash(item.deliveredOn)}
              {item.supplierName ? ` · ${item.supplierName}` : ''}
              {` · Qty ${item.quantity ?? '—'} ${item.unitOfMeasure}`}
              {` · ${formatPeso(item.unitPrice)}`}
            </div>
            <div className="fs-10 text-body-tertiary">{linkedLabel}</div>
            {isPastInvoice && !item.isLinkedToCurrentItem ? (
              <div className="fs-10 text-warning">
                Dated before this purchase order — viewing only
              </div>
            ) : null}
          </div>
        }
      />
    );
  };

  if (isLoading) {
    return (
      <div className="d-flex align-items-center gap-2 py-3">
        <Spinner size="sm" animation="border" />
        <span>Loading invoices that match this product…</span>
      </div>
    );
  }

  if (errorMessage) {
    return <p className="mb-0 text-danger">{errorMessage}</p>;
  }

  if (items.length === 0 && !hasCurrentLink) {
    return (
      <p className="mb-0">
        No procurement invoices were found for this product from this supplier
        (or a linked supplier).
      </p>
    );
  }

  return (
    <Form>
      <p className="mb-3 text-body-tertiary">
        Choose an invoice dated on or after this purchase order. Binding it
        replaces the current invoice on this line.
      </p>
      {hasCurrentLink ? (
        <Form.Check
          type="radio"
          id="bind-invoice-item-remove"
          name="satisfying-invoice-item"
          className="mb-3"
          checked={selectedId === REMOVE_INVOICE_SELECTION}
          onChange={() => setSelectedId(REMOVE_INVOICE_SELECTION)}
          label={
            <div>
              <div className="fw-semibold">Remove invoice</div>
              <div className="fs-9 text-body-secondary">
                Unlink the procurement invoice from this purchase order item.
              </div>
            </div>
          }
        />
      ) : null}
      {defaultItems.length === 0 ? (
        <p className="mb-3">
          No procurement invoices dated on or after this purchase order were
          found for this product.
        </p>
      ) : (
        defaultItems.map(item => renderItem(item, true))
      )}
      {pastItems.length > 0 && !showPastInvoices ? (
        <Button
          variant="link"
          className="p-0 fs-9"
          onClick={() => setShowPastInvoices(true)}
        >
          See more ({pastItems.length} earlier invoice
          {pastItems.length === 1 ? '' : 's'})
        </Button>
      ) : null}
      {showPastInvoices && pastItems.length > 0 ? (
        <div className="mt-2">
          <div className="d-flex justify-content-between align-items-center mb-2">
            <p className="mb-0 fs-9 text-body-secondary">
              Earlier invoices (before this purchase order)
            </p>
            <Button
              variant="link"
              className="p-0 fs-9"
              onClick={() => setShowPastInvoices(false)}
            >
              Hide
            </Button>
          </div>
          {pastItems.map(item => renderItem(item, false))}
        </div>
      ) : null}
    </Form>
  );
});

BindPurchaseOrderItemToInvoiceForm.displayName =
  'BindPurchaseOrderItemToInvoiceForm';

export default BindPurchaseOrderItemToInvoiceForm;
